import { Box, Typography, Chip, alpha, useTheme } from "@mui/material";
import { PanelShell, StatGrid, StatTile, PanelUnavailable, FindingList } from "./PanelShell";

const FAMILY_LABELS = {
  accelerometer: "Accelerometer",
  gyroscope: "Gyroscope",
  compass: "Compass",
  barometer: "Barometer",
  airspeed: "Airspeed",
};

/**
 * The aircraft's own bill of materials, read out of its parameters, plus how
 * the board held up. Bus and address are shown because that is what separates
 * the internal compass from the one on the mast.
 */
const HardwarePanel = ({ module }) => {
  const theme = useTheme();
  const panel = module?.panel;
  if (!panel) return <PanelUnavailable reason={module?.skip_reason || "No hardware information in this log."} />;

  const { board = {}, sensors = {}, power = {}, scheduler = {}, logged_messages: messages = [] } = panel;
  const families = Object.entries(sensors);

  const subtitle = board.firmware_string
    ? String(board.firmware_string)
    : board.major != null
      ? `Firmware ${board.major}.${board.minor}.${board.patch}${board.board_type ? ` · board type ${board.board_type}` : ""}`
      : "Sensor inventory from the aircraft's parameters";

  return (
    <PanelShell title="Hardware" subtitle={subtitle} health={module.health}>
      <StatGrid>
        {power.vcc_min != null && (
          <StatTile
            label="5 V rail min"
            value={power.vcc_min}
            unit="V"
            tone={power.vcc_min < 4.3 ? "bad" : power.vcc_min < 4.6 ? "warn" : "good"}
          />
        )}
        {power.vcc_max != null && <StatTile label="5 V rail max" value={power.vcc_max} unit="V" />}
        {power.servo_rail_min != null && <StatTile label="Servo rail min" value={power.servo_rail_min} unit="V" />}
        {power.mcu_temp_max_c != null && <StatTile label="MCU peak temp" value={power.mcu_temp_max_c} unit="°C" />}
        {scheduler.max_load != null && (
          <StatTile
            label="Peak CPU load"
            value={Math.round(scheduler.max_load * 100)}
            unit="%"
            tone={scheduler.max_load > 0.85 ? "warn" : "good"}
          />
        )}
        {scheduler.long_loops != null && (
          <StatTile
            label="Long loops"
            value={scheduler.long_loops}
            tone={scheduler.long_loops > 50 ? "warn" : "good"}
            hint="Control loops that ran late"
          />
        )}
        {scheduler.min_free_mem != null && (
          <StatTile label="Free memory" value={(scheduler.min_free_mem / 1024).toFixed(1)} unit="KB" />
        )}
      </StatGrid>

      {families.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: "uppercase" }}>
            Sensor inventory
          </Typography>
          <Box
            sx={{
              mt: 1,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: 1.5,
            }}
          >
            {families.map(([family, devices]) => (
              <Box
                key={family}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>
                    {FAMILY_LABELS[family] || family}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ ml: "auto", fontFamily: "monospace", color: theme.palette.text.secondary }}
                  >
                    ×{devices.length}
                  </Typography>
                </Box>
                {devices.map((d) => (
                  <Box
                    key={d.param}
                    sx={{ display: "flex", gap: 1, alignItems: "baseline", fontFamily: "monospace", fontSize: 12 }}
                    title={`${d.param} = ${d.devid}`}
                  >
                    <Box component="span" sx={{ color: theme.palette.text.secondary }}>
                      #{d.instance + 1}
                    </Box>
                    <Box component="span" sx={{ color: "#3a8eff" }}>
                      {d.bus_type}
                    </Box>
                    <Box component="span" sx={{ color: theme.palette.text.secondary }}>
                      bus {d.bus} · addr 0x{d.address.toString(16).toUpperCase()}
                    </Box>
                    <Box component="span" sx={{ ml: "auto", color: theme.palette.text.disabled }}>
                      type {d.device_type}
                    </Box>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {messages.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: "uppercase" }}>
            Message types logged
          </Typography>
          <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {messages.map((m) => (
              <Chip key={m} label={m} size="small" variant="outlined" sx={{ fontFamily: "monospace", fontSize: 11 }} />
            ))}
          </Box>
          <Typography variant="caption" sx={{ display: "block", mt: 1, color: theme.palette.text.secondary }}>
            What is missing here matters as much as what is present — an analysis can only see what the
            firmware was told to log.
          </Typography>
        </Box>
      )}

      <FindingList findings={module.findings} />
    </PanelShell>
  );
};

export default HardwarePanel;
